import { BackTop, Table, Card, Row, Col, Statistic } from "antd";
import React, { useState, useEffect } from "react";
import BillsFilterBar from "../../../components/filter-bars/bills-report-filters";
import { cedisLocale, exportToExcel } from "../../../helpers/utilities";
import { getUser } from "../../../helpers/auth";
import { format } from "date-fns";
import { Badge } from "@mantine/core";

export default function BillsReport({ data, isLoading }) {
  const [filteredData, setFilteredData] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleReset = () => {
    setFilteredData(data);
    setExpandedRowKeys([]);
  };

  const handleApplyFilter = (newData) => {
    setFilteredData(newData);
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Calculate amount due for a bill
  const calculateAmountDue = (bill) => {
    // Calculate subtotal from items
    const itemsSubtotal =
      bill.items?.reduce(
        (acc, item) =>
          acc + (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0),
        0
      ) || 0;

    // Add charges
    const chargesTotal =
      bill.charges?.reduce(
        (acc, charge) => acc + (parseFloat(charge.value) || 0),
        0
      ) || 0;

    return itemsSubtotal + chargesTotal;
  };

  // Calculate amount paid for a bill
  const calculateAmountPaid = (bill) => {
    return (
      bill.payments?.reduce(
        (acc, payment) => acc + (parseFloat(payment.amount) || 0),
        0
      ) || 0
    );
  };

  // Calculate total discounts for a bill (bill-level + line item discounts)
  const calculateTotalDiscounts = (bill) => {
    // Bill-level discount
    const billDiscount = parseFloat(bill.discount) || 0;
    const billDiscountType = bill.discountType || "value";

    // Calculate bill discount amount based on type
    let billDiscountAmount = 0;
    if (billDiscount > 0) {
      const billSubtotal = calculateAmountDue(bill);
      billDiscountAmount =
        billDiscountType === "percent"
          ? (billSubtotal * billDiscount) / 100
          : billDiscount;
    }

    // Line item discounts
    const lineItemDiscounts =
      bill.items?.reduce((acc, item) => {
        const itemDiscount = parseFloat(item.discount) || 0;
        const itemDiscountType = item.discountType || "value";

        if (itemDiscount > 0) {
          const itemSubtotal =
            (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0);
          const itemDiscountAmount =
            itemDiscountType === "percent"
              ? (itemSubtotal * itemDiscount) / 100
              : itemDiscount;
          return acc + itemDiscountAmount;
        }
        return acc;
      }, 0) || 0;

    return billDiscountAmount + lineItemDiscounts;
  };

  // Calculate metrics for the current filtered data
  const calculateMetrics = () => {
    const totalBills = filteredData?.length;
    const totalAmountDue = filteredData?.reduce(
      (sum, bill) => sum + calculateAmountDue(bill),
      0
    );
    const totalAmountPaid = filteredData?.reduce(
      (sum, bill) => sum + calculateAmountPaid(bill),
      0
    );
    const totalDiscounts = filteredData?.reduce(
      (sum, bill) => sum + calculateTotalDiscounts(bill),
      0
    );
    const paidBills =
      filteredData?.filter((bill) => calculateAmountPaid(bill) > 0)?.length ||
      0;
    const unpaidBills = totalBills - paidBills;
    const collectionRate =
      totalBills > 0 ? (totalAmountPaid / totalAmountDue) * 100 : 0;

    return {
      totalBills,
      totalAmountDue,
      totalAmountPaid,
      totalDiscounts,
      paidBills,
      unpaidBills,
      collectionRate,
    };
  };

  // Columns for the main table
  const columns = [
    {
      title: "#",
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => ++index,
    },
    {
      title: "Date",
      sorter: (a, b) => a.createdAt > b.createdAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        format(new Date(record.createdAt), "MMMM d, yyyy"),
    },
    {
      title: "Member",
      sorter: (a, b) => a.patient.firstName > b.patient.firstName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record.patient.firstName}  ${record.patient.lastName}`,
    },
    {
      title: "Bill Number",
      dataIndex: ["billNumber"],
      sorter: (a, b) => a.billNumber > b.billNumber,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Due",
      sorter: (a, b) => calculateAmountDue(a) > calculateAmountDue(b),
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${cedisLocale.format(calculateAmountDue(record))}`,
    },
    {
      title: "Discounts",
      sorter: (a, b) => calculateTotalDiscounts(a) > calculateTotalDiscounts(b),
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => {
        const totalDiscounts = calculateTotalDiscounts(record);
        return totalDiscounts > 0
          ? `${cedisLocale.format(totalDiscounts)}`
          : "-";
      },
    },
    {
      title: "Paid",
      sorter: (a, b) => calculateAmountPaid(a) > calculateAmountPaid(b),
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${cedisLocale.format(calculateAmountPaid(record))}`,
    },
    {
      title: "Status",
      sorter: (a, b) => calculateAmountPaid(a) > calculateAmountPaid(b),
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => {
        const amountPaid = calculateAmountPaid(record);
        return (
          <Badge color={`${amountPaid > 0 ? "green" : "red"}`}>
            {amountPaid > 0 ? "Paid" : "Unpaid"}
          </Badge>
        );
      },
    },
  ];

  // Columns for the expanded rows (bill items)
  const expandedRowColumns = [
    {
      title: "Service Name",
      dataIndex: "serviceName",
      key: "serviceName",
      render: (text, record) => record?.service?.serviceName || "N/A",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (text, record) =>
        `${cedisLocale.format(parseFloat(record.rate) || 0)}`,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record) => parseFloat(record.quantity) || 0,
    },
    {
      title: "Discount",
      key: "discount",
      render: (text, record) => {
        const discount = parseFloat(record.discount) || 0;
        const discountType = record.discountType || "value";

        if (discount > 0) {
          const itemSubtotal =
            (parseFloat(record.rate) || 0) * (parseFloat(record.quantity) || 0);
          const discountAmount =
            discountType === "percent"
              ? (itemSubtotal * discount) / 100
              : discount;
          return `${cedisLocale.format(discountAmount)} (${
            discountType === "percent" ? `${discount}%` : "Fixed"
          })`;
        }
        return "GHS 0.00";
      },
    },
    {
      title: "Total",
      key: "total",
      render: (text, record) => {
        const total =
          (parseFloat(record.rate) || 0) * (parseFloat(record.quantity) || 0);
        return `${cedisLocale.format(total)}`;
      },
    },
  ];

  // Expanded row renderer
  const expandedRowRender = (record) => {
    const billItems = record.items || [];
    const billCharges = record.charges || [];
    const itemsSubtotal =
      billItems?.reduce(
        (acc, item) =>
          acc + (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0),
        0
      ) || 0;
    const chargesTotal = billCharges.reduce(
      (acc, charge) => acc + (parseFloat(charge.value) || 0),
      0
    );

    return (
      <div className="p-3">
        <h6 className="mb-3">Bill Items</h6>
        <Table
          columns={expandedRowColumns}
          dataSource={billItems}
          pagination={false}
          size="small"
          bordered
          rowKey={(item, index) => `${record.id}-item-${index}`}
        />

        {billCharges.length > 0 && (
          <>
            <h6 className="mb-3 mt-4">Bill Charges</h6>
            <Table
              columns={[
                {
                  title: "Charge Name",
                  key: "chargeName",
                  render: (text, record) => record?.charge?.chargeName || "N/A",
                },
                {
                  title: "Value",
                  key: "value",
                  render: (text, record) =>
                    `${cedisLocale.format(parseFloat(record.value) || 0)}`,
                },
              ]}
              dataSource={billCharges}
              pagination={false}
              size="small"
              bordered
              rowKey={(charge, index) => `${record.id}-charge-${index}`}
            />
          </>
        )}

        <div className="mt-3 text-end">
          <div className="mb-2">
            <strong>
              Items Subtotal: GHS {cedisLocale.format(itemsSubtotal)}
            </strong>
          </div>
          {chargesTotal > 0 && (
            <div className="mb-2">
              <strong>Charges: GHS {cedisLocale.format(chargesTotal)}</strong>
            </div>
          )}
          <div className="mb-2">
            <strong>
              Gross Total: GHS {cedisLocale.format(calculateAmountDue(record))}
            </strong>
          </div>
          {calculateTotalDiscounts(record) > 0 && (
            <div className="mb-2 text-success">
              <strong>
                Total Discounts: GHS{" "}
                {cedisLocale.format(calculateTotalDiscounts(record))}
              </strong>
            </div>
          )}
          <div>
            <strong>
              Net Amount: GHS{" "}
              {cedisLocale.format(
                calculateAmountDue(record) - calculateTotalDiscounts(record)
              )}
            </strong>
          </div>
        </div>
      </div>
    );
  };

  const metrics = calculateMetrics();

  return (
    <div>
      <BillsFilterBar
        data={data}
        onReset={handleReset}
        onFilter={(d) => handleApplyFilter(d)}
        excelExport={() =>
          exportToExcel(
            "billsReportTable",
            `${getUser().facility.facilityName}-bills-report`
          )
        }
      />

      {/* Metrics Cards */}
      <Row gutter={16} className="mt-3 mb-3">
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Bills"
              value={metrics.totalBills}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Amount Due"
              value={cedisLocale.format(metrics.totalAmountDue)}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Discounts"
              value={cedisLocale.format(metrics.totalDiscounts)}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Amount Paid"
              value={cedisLocale.format(metrics.totalAmountPaid)}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Unpaid Bills"
              value={metrics.unpaidBills}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Collection Rate"
              value={metrics.collectionRate.toFixed(1)}
              suffix="%"
              valueStyle={{
                color:
                  metrics.collectionRate >= 80
                    ? "#3f8600"
                    : metrics.collectionRate >= 60
                    ? "#faad14"
                    : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        className="border mt-3"
        id="billsReportTable"
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        bordered
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => record?.items && record?.items?.length > 0,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([record.id]);
            } else {
              setExpandedRowKeys([]);
            }
          },
        }}
      />
      <BackTop />
    </div>
  );
}
